import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "src/lib/supabaseClient";

export type DiagnosticSubject = "math" | "cs";
export type DiagnosticQuestionType = "TF" | "MCQ-SC" | "MCQ-MC" | "FIB";

export interface ContentDocument {
  schema_version?: number;
  blocks?: Array<Record<string, unknown>>;
}

export interface DiagnosticOption {
  id: string;
  content?: ContentDocument | string;
  content_block?: ContentDocument | string;
}

export interface DiagnosticQuestion {
  id: string;
  order: number;
  content: ContentDocument;
  type: DiagnosticQuestionType;
  options: DiagnosticOption[];
}

export interface StartDiagnosticPayload {
  subject: DiagnosticSubject;
  gradeId?: string | null;
  boardId?: string | null;
  csCourse?: string | null;
  guestName: string;
  guestEmail: string;
  captchaToken: string;
}

export interface StartDiagnosticResult {
  attemptId: string;
  attemptToken: string;
  quiz: {
    title: string;
    durationSeconds: number;
    questions: DiagnosticQuestion[];
  };
}

export interface SavedAnswer {
  questionId: string;
  selectedAnswer: unknown;
  revision: number;
}

export interface AttemptStatus {
  status: "active" | "completed" | "expired";
  attemptId: string;
  completedAt: string | null;
  startedAt: string;
  expiresAt: string;
  durationSeconds: number;
  remainingSeconds: number;
  quiz: {
    title: string;
    questions: DiagnosticQuestion[];
  };
  answers: SavedAnswer[];
}

export interface ReportSummary {
  schema_version?: number;
  overall?: {
    correct?: number;
    incorrect?: number;
    unanswered?: number;
    total?: number;
    score_pct?: number;
    points_earned?: number;
    points_total?: number;
    status?: string;
  };
  narrative?: string;
  timing?: { configured_seconds?: number; elapsed_seconds?: number };
}

export interface ReportPayload {
  attempt: {
    id: string;
    completedAt: string | null;
    guestName: string;
    pdfPath: string | null;
  };
  quiz: {
    title: string;
    subjectName: string;
    gradeName: string | null;
    boardName: string | null;
    courseTrack: string | null;
  };
  summary: ReportSummary;
}

const FAILED = "The assessment could not be reached. Try again in a moment.";

export function attemptTokenKey(attemptId: string) {
  return `diag_token_${attemptId}`;
}

async function messageFromInvokeError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json();
      if (typeof body?.message === "string" && body.message.trim()) return body.message;
    } catch {
      return FAILED;
    }
  }
  return FAILED;
}

async function invoke<T>(name: string, body: Record<string, unknown>, token?: string): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: token ? { "X-Attempt-Token": token } : undefined,
  });
  if (error) throw new Error(await messageFromInvokeError(error));
  return data as T;
}

export async function startDiagnostic(payload: StartDiagnosticPayload): Promise<StartDiagnosticResult> {
  return invoke<StartDiagnosticResult>("start-diagnostic", {
    subject: payload.subject,
    gradeId: payload.gradeId ?? null,
    boardId: payload.boardId ?? null,
    csCourse: payload.csCourse ?? null,
    guestName: payload.guestName,
    guestEmail: payload.guestEmail,
    captchaToken: payload.captchaToken,
  });
}

export async function fetchAttemptStatus(attemptId: string, token: string): Promise<AttemptStatus> {
  return invoke<AttemptStatus>("attempt-status", { attemptId }, token);
}

export async function saveDiagnosticResponse(
  attemptId: string,
  token: string,
  questionId: string,
  selectedAnswer: unknown,
  revision: number,
): Promise<{ revision: number }> {
  return invoke("save-response", { attemptId, questionId, selectedAnswer, revision }, token);
}

export async function submitDiagnostic(attemptId: string, token: string): Promise<ReportPayload> {
  return invoke<ReportPayload>("submit-diagnostic", { attemptId }, token);
}

export async function fetchReport(attemptId: string, token: string): Promise<ReportPayload> {
  return invoke<ReportPayload>("report-data", { attemptId }, token);
}

export interface MathGradeOption {
  id: string;
  label: string;
  order: number;
  countryId: number;
  countryName: string;
}

export interface MathBoardOption {
  id: string;
  label: string;
  gradeId: string;
}

/** Used only when public.countries is not readable. Codes match grade_id prefixes. */
const COUNTRY_NAME_BY_CODE: Record<string, string> = {
  US: "United States",
  UK: "United Kingdom",
  CA: "Canada",
  AE: "United Arab Emirates",
  AU: "Australia",
};

export async function fetchMathParameters(): Promise<{
  grades: MathGradeOption[];
  boards: MathBoardOption[];
}> {
  const [gradesResult, boardsResult, countriesResult] = await Promise.all([
    supabase.from("grades").select("grade_id, grade_label, grade_order, country_id").order("grade_order"),
    supabase.from("grade_board_map").select("grade_id, board_id, boards(board_name)"),
    supabase.from("countries").select("country_id, country_code, country_name").order("country_id"),
  ]);

  if (gradesResult.error || boardsResult.error) {
    throw new Error("Grades and boards could not be loaded.");
  }

  const nameById = new Map<number, string>();
  const nameByCode = new Map<string, string>(Object.entries(COUNTRY_NAME_BY_CODE));
  for (const country of countriesResult.data ?? []) {
    nameById.set(country.country_id, country.country_name);
    nameByCode.set(country.country_code, country.country_name);
  }

  const boards = (boardsResult.data ?? []).map((row) => {
    const board = Array.isArray(row.boards) ? row.boards[0] : row.boards;
    return {
      id: row.board_id as string,
      label: (board?.board_name as string | undefined) ?? (row.board_id as string),
      gradeId: row.grade_id as string,
    };
  });

  return {
    grades: (gradesResult.data ?? []).map((grade) => {
      const code = grade.grade_id.split("-")[0] ?? "";
      return {
        id: grade.grade_id,
        label: grade.grade_label,
        order: grade.grade_order,
        countryId: grade.country_id,
        countryName: nameById.get(grade.country_id) ?? nameByCode.get(code) ?? code,
      };
    }),
    boards,
  };
}
